import axios from 'axios';
import store from '../store';
import { normalizeSupportCitations } from './SupportCitationParser';
import type {
  SupportCitation,
  SupportVideoPlayback,
} from './SupportCitationParser';

export type {
  SupportCitation,
  SupportVideoPlayback,
} from './SupportCitationParser';

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_ROOT,
  withCredentials: false,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

export interface MetricsChatSummary {
  metricsChatId: number;
  title: string;
  createdDateUtc: string;
}

export interface MetricsChatMessage {
  metricsChatMessageId: number;
  role: 'user' | 'assistant';
  content: string;
  citations: SupportCitation[];
  aiQuerySourceMessageId: number | null;
  createdDateUtc: string;
}

export interface MetricsChatDetail {
  metricsChatId: number;
  title: string;
  createdDateUtc: string;
  messages: MetricsChatMessage[];
}

export interface MetricsChatResponse {
  metricsChatId: number;
  sourceMessageId: number;
  queued: boolean;
}

export default {
  /**
   * Lists all non-deleted chats for the authenticated user.
   */
  async getChats(): Promise<MetricsChatSummary[]> {
    const token = store.getters.token;
    const response = await apiClient.get('/admin/metrics/chats', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  /**
   * Creates a new chat and sends the first question.
   */
  async createChat(question: string): Promise<MetricsChatResponse> {
    const token = store.getters.token;
    const response = await apiClient.post(
      '/admin/metrics/chats',
      { question },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /**
   * Gets a chat with all its messages.
   */
  async getChatDetail(id: number): Promise<MetricsChatDetail> {
    const token = store.getters.token;
    const response = await apiClient.get(`/admin/metrics/chats/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      ...response.data,
      messages: response.data.messages.map(normalizeMetricsChatMessage),
    };
  },

  /**
   * Sends a follow-up question in an existing chat.
   */
  async sendMessage(
    id: number,
    question: string
  ): Promise<MetricsChatResponse> {
    const token = store.getters.token;
    const response = await apiClient.post(
      `/admin/metrics/chats/${id}/messages`,
      { question },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /**
   * Soft-deletes a chat.
   */
  async deleteChat(id: number): Promise<void> {
    const token = store.getters.token;
    await apiClient.delete(`/admin/metrics/chats/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  /**
   * Updates the title of a chat.
   */
  async updateTitle(id: number, title: string): Promise<void> {
    const token = store.getters.token;
    await apiClient.patch(
      `/admin/metrics/chats/${id}`,
      { title },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  },

  /**
   * Requests a five-minute PDF address after authenticated ownership and citation checks.
   */
  async getSupportSourceUrl(
    metricsChatId: number,
    messageId: number,
    documentId: string
  ): Promise<string> {
    const token = store.getters.token;
    const response = await apiClient.get<{ url: string }>(
      `/admin/metrics/chats/${metricsChatId}/messages/${messageId}/sources/${encodeURIComponent(
        documentId
      )}/link`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data.url;
  },

  /**
   * Requests a short-lived playback address for a cited private support video.
   * The address is a credential: keep it in memory, never persist or log it.
   */
  async getSupportVideoPlayback(
    metricsChatId: number,
    messageId: number,
    documentId: string
  ): Promise<SupportVideoPlayback> {
    const token = store.getters.token;
    const response = await apiClient.get(
      `/admin/metrics/chats/${metricsChatId}/messages/${messageId}/sources/${encodeURIComponent(
        documentId
      )}/playback`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
};

function normalizeMetricsChatMessage(message: any): MetricsChatMessage {
  return {
    metricsChatMessageId: message.metricsChatMessageId,
    role: message.role,
    content: message.content,
    citations: normalizeSupportCitations(
      message.citationJson,
      message.citations
    ),
    aiQuerySourceMessageId: message.aiQuerySourceMessageId ?? null,
    createdDateUtc: message.createdDateUtc,
  };
}
